import { HeartPulse } from 'lucide-react';
import { NEEDS, NEED_KEYS, healthOf, type HealthContent, type Sickness } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, idFor, type NumberSpec } from './form';

type HealthNumbers = Exclude<keyof HealthContent, 'visible' | 'sicknesses' | 'clinicPlaceId' | 'doctorActivityId'>;
const FALLING_ILL: NumberSpec<HealthNumbers>[] = [
  { key: 'redBelow', label: 'A need is in the red below', unit: 'number', min: 1, max: 60, whole: true },
  { key: 'redNeeds', label: 'Needs in the red for a bad hour', unit: 'number', min: 1, max: 6, whole: true },
  { key: 'sickFrom', label: 'Neglect before falling ill is possible', unit: 'number', min: 1, max: 100, whole: true },
  { key: 'chanceStep', label: 'Chance an hour, per point of neglect', unit: 'percent', min: 0, max: 1 },
  { key: 'chanceCap', label: 'Highest chance an hour', unit: 'percent', min: 0, max: 1 },
];
const GETTING_WORSE: NumberSpec<HealthNumbers>[] = [
  {
    key: 'recoverHours',
    label: 'Good hours in a row to get better alone',
    unit: 'number',
    min: 1,
    max: 168,
    whole: true,
  },
  { key: 'wellAbove', label: 'A good hour: every need at least', unit: 'number', min: 1, max: 100, whole: true },
  {
    key: 'seriousAfterHours',
    label: 'Mild turns serious after (hours)',
    unit: 'number',
    min: 1,
    max: 336,
    whole: true,
  },
  {
    key: 'hospitalAfterHours',
    label: 'Serious means hospital after (hours)',
    unit: 'number',
    min: 1,
    max: 336,
    whole: true,
  },
];
const TREATMENT: NumberSpec<HealthNumbers>[] = [
  { key: 'pharmacyPence', label: "The pharmacy's medicine, delivered", unit: 'pounds', min: 0, max: 100_000_000 },
  { key: 'prescriptionPence', label: 'Prescription charge (the GP)', unit: 'pounds', min: 0, max: 100_000_000 },
  { key: 'hospitalPence', label: 'Hospital bill', unit: 'pounds', min: 0, max: 100_000_000 },
  { key: 'hospitalSeconds', label: 'Time kept in hospital (seconds)', unit: 'number', min: 10, max: 3600, whole: true },
];
const CAUSES = ['any', ...NEED_KEYS] as Sickness['cause'][];
const causeLabel = (cause: Sickness['cause']) =>
  cause === 'any' ? 'Anything (when no single need fits)' : `${NEEDS[cause].icon} ${NEEDS[cause].label} let go`;

// Health (as Lagos Life's 💊 Health): the sicknesses and what brings them on, how falling ill and getting worse
// work, and what treatment costs (the NHS is free; the prescription charge is England's). Part of the content
// draft, published with it.
export function HealthPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const health = healthOf(working ?? {});
  const change = (fn: (draft: HealthContent) => void) =>
    editor.edit((content) => {
      content.health ??= structuredClone(healthOf(content));
      fn(content.health);
    });
  const sickness = (id: string, fn: (entry: Sickness) => void) =>
    change((draft) => {
      const entry = draft.sicknesses.find((item) => item.id === id);
      if (entry) fn(entry);
    });
  const numbers = (specs: NumberSpec<HealthNumbers>[]) =>
    specs.map((spec) => (
      <NumberField
        key={spec.key}
        id={`health-${spec.key}`}
        spec={spec}
        value={health[spec.key]}
        onChange={(value) => change((draft) => void (draft[spec.key] = value))}
      />
    ));
  const clinicActivities = (working?.activities ?? []).filter((activity) => activity.placeId === health.clinicPlaceId);
  return (
    <section>
      <PageHeader
        title="Health"
        description="Falling ill, getting worse and getting treated (as Lagos Life's Health). Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Health">
            <h2 className="content-subtitle">
              <HeartPulse size={16} aria-hidden="true" /> Health
            </h2>
            <label className="designer-check">
              <Switch
                checked={health.visible}
                aria-label="Health on"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can fall ill and be treated
            </label>
            <div className="content-grid">
              <div className="admin-field">
                <Label htmlFor="health-clinic">The clinic</Label>
                <select
                  id="health-clinic"
                  className="content-select"
                  value={health.clinicPlaceId}
                  onChange={(e) => change((draft) => void (draft.clinicPlaceId = e.target.value))}
                >
                  {working.places.map((place) => (
                    <option key={place.id} value={place.id}>
                      {place.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-field">
                <Label htmlFor="health-doctor">The GP (an activity at the clinic)</Label>
                <select
                  id="health-doctor"
                  className="content-select"
                  value={health.doctorActivityId}
                  onChange={(e) => change((draft) => void (draft.doctorActivityId = e.target.value))}
                >
                  {!clinicActivities.some((activity) => activity.id === health.doctorActivityId) && (
                    <option value={health.doctorActivityId}>Choose one</option>
                  )}
                  {clinicActivities.map((activity) => (
                    <option key={activity.id} value={activity.id}>
                      {activity.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Falling ill">
            <h2 className="content-subtitle">Falling ill</h2>
            <p className="admin-muted">
              Checked every hour: a bad hour adds a point of neglect for each need in the red, a better one takes a
              point off. From the threshold, each hour has a growing chance of falling ill.
            </p>
            <div className="content-grid">{numbers(FALLING_ILL)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Getting better or worse">
            <h2 className="content-subtitle">Getting better or worse</h2>
            <div className="content-grid">{numbers(GETTING_WORSE)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Treatment">
            <h2 className="content-subtitle">Treatment</h2>
            <p className="admin-muted">
              The pharmacy treats mild illness only; the GP treats anything. The NHS is free at the point of use, so the
              hospital bill starts at £0.
            </p>
            <div className="content-grid">{numbers(TREATMENT)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Sicknesses">
            <h2 className="content-subtitle">Sicknesses</h2>
            {health.sicknesses.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`sickness-${item.id}`}>
                <Field id={`sickness-${item.id}-name`} label="Name">
                  <Input
                    id={`sickness-${item.id}-name`}
                    value={item.name}
                    maxLength={40}
                    onChange={(e) => sickness(item.id, (entry) => void (entry.name = e.target.value))}
                  />
                </Field>
                <Field id={`sickness-${item.id}-emoji`} label="Emoji">
                  <Input
                    id={`sickness-${item.id}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => sickness(item.id, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <Field id={`sickness-${item.id}-mild`} label="Mild">
                  <Input
                    id={`sickness-${item.id}-mild`}
                    value={item.mild}
                    maxLength={160}
                    onChange={(e) => sickness(item.id, (entry) => void (entry.mild = e.target.value))}
                  />
                </Field>
                <Field id={`sickness-${item.id}-serious`} label="Serious">
                  <Input
                    id={`sickness-${item.id}-serious`}
                    value={item.serious}
                    maxLength={160}
                    onChange={(e) => sickness(item.id, (entry) => void (entry.serious = e.target.value))}
                  />
                </Field>
                <div className="admin-field">
                  <Label htmlFor={`sickness-${item.id}-cause`}>Brought on by</Label>
                  <select
                    id={`sickness-${item.id}-cause`}
                    className="content-select"
                    value={item.cause}
                    onChange={(e) =>
                      sickness(item.id, (entry) => void (entry.cause = e.target.value as Sickness['cause']))
                    }
                  >
                    {CAUSES.map((cause) => (
                      <option key={cause} value={cause}>
                        {causeLabel(cause)}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={health.sicknesses.length <= 1}
                  onClick={() =>
                    change(
                      (draft) => void (draft.sicknesses = draft.sicknesses.filter((entry) => entry.id !== item.id)),
                    )
                  }
                >
                  Remove {item.name}
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change((draft) =>
                  draft.sicknesses.push({
                    id: idFor('New sickness', new Set(draft.sicknesses.map((entry) => entry.id)), 'sickness', 30),
                    name: 'New sickness',
                    emoji: '🤒',
                    mild: 'Feeling under the weather.',
                    serious: 'It has knocked you down properly.',
                    cause: 'any',
                  }),
                )
              }
            >
              Add a sickness
            </Button>
          </fieldset>
        </div>
      )}
    </section>
  );
}
